import { chunk } from "lodash";

import {
  Asset,
  AssetDTO,
  AssetFilters,
  AssetGroupBy,
  AssetsCountResponse,
  AssetUpdate,
  NewAssetDTO,
} from "../models/assets";
import {
  AssetQueryOptions,
  BulkAssetQueryRequest,
} from "../models/bulkActions";
import { buildCustomFieldFilters } from "../models/customFields";
import { PostBasedPaginatedResponse } from "../models/shared";
import {
  AssetsPaginatedResponse,
  AssetsResponse,
  CursorState,
  ProjectId,
  Sort,
  UUIDv4,
} from "../types";
import { ASSET_MAXIMUM_BATCH_SIZE, Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import {
  buildSearchString,
  buildSortString,
  fetchAllPaginatedResults,
} from "./helpers";

const defaultListFilters: AssetFilters = {
  includeCustomFields: true,
};

// #2501 — the exact wire shape `getAssetsAPIClient.ts:query` (app) has always
// sent for `assets:query`. `models/assets.ts`'s `AssetQueryResponse`/
// `AssetQueryRequest` family is a separate, currently-unused, and internally
// inconsistent type (its `entityType` literal is still the v1
// "assets_atomic_query") — deliberately not reused here so this method's
// typing matches its actual runtime behavior. See the assetClient.query
// doc-comment below and the #2501 PR description for the full reconciliation
// reasoning.
type AssetQueryV2Response = PostBasedPaginatedResponse<
  AssetDTO,
  BulkAssetQueryRequest
>;

// Sent only when the caller opts in (ILMARCH-61). One place to update
// if the BE changes the accepted value in the future.
const COMPLETION_TRACKING_INCLUDE = "current" as const;

export type AssetClientOptions = {
  includeStatusCompletionsTracking?: boolean;
  // Opt-in — appended to read requests only when the caller's `enable-asset-classifications`
  // flag is on.
  includeClassificationValues?: boolean;
};

export type AssetClientConfig = {
  http: AssetsHttpClient;
  projectId: ProjectId;
  clientOptions?: AssetClientOptions;
};

export const getAssetClient = ({
  http,
  projectId,
  clientOptions,
}: AssetClientConfig) => {
  const includeStatusCompletionsTracking =
    clientOptions?.includeStatusCompletionsTracking ?? false;
  const includeClassificationValues = clientOptions?.includeClassificationValues ?? false;
  return {
    get: async (
      id: Asset["id"],
      options?: Pick<AssetFilters, "includeDeleted" | "includeCustomFields">
    ): Promise<AssetDTO> => {
      const searchString = buildSearchString({
        ...(options?.includeCustomFields !== undefined && {
          includeCustomAttributes: options.includeCustomFields,
        }),
        ...(options?.includeDeleted !== undefined && {
          includeDeleted: options.includeDeleted,
        }),
        ...(includeStatusCompletionsTracking && {
          includeStatusCompletionsTracking: COMPLETION_TRACKING_INCLUDE,
        }),
        ...(includeClassificationValues && { includeClassificationValues: true }),
      });

      return await http.get<AssetDTO>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${Endpoints.Assets}/${id}${
          searchString ? `?${searchString}` : ""
        }`
      );
    },

    /*
    Return list of all AssetDTO matches with filter criteria if fetchAll option is
    supplied, otherwise return one page (AssetsPaginatedResponse).

    #2501 — this builds the `assets_atomic_query_v2` request body inline rather
    than via the shared `buildQuery` helper (clients/helpers.ts) that
    AssetsQuickReportsPanel/AssetsExportPanel/buildBulkActionRequestPayload use
    for bulk-action requests. That helper intentionally doesn't support
    `statusLabels`/`recoverable`, and extending it would have silently added
    those filters to bulk delete/restore/edit/report/export requests too — an
    out-of-scope behavior change for callers this issue doesn't touch. This
    method is the single superset implementation for both the app's original
    positional `getAssetsAPIClient.query(cursorState, filters, sort, options)`
    (which now delegates here — see the app adapter) and shared-web/
    viewer-extensions' existing `AssetQueryOptions`-based callers.
    */
    query: async (
      options: AssetQueryOptions
    ): Promise<AssetQueryV2Response> => {
      const {
        assetFilters = {},
        selectedAsset = [],
        cursorState,
        fetchAll,
        assetIds,
        includeTotalResults,
        includeLastPageLink,
        orderBy,
        signal,
      } = options;

      const filters = {
        ...defaultListFilters,
        ...assetFilters,
      };
      const {
        limit,
        categoryId,
        categoryIds,
        assignedCategoryIds,
        location,
        statusIds,
        statusLabels,
        customFields,
        classifications,
        searchText,
        sourceModelLineageUrns,
        recoverable,
        parentSystemIds,
        ancestorSystemIds,
        includeCustomFields,
        includeDeleted,
        includedStandardFields,
        includedComputedFields,
      } = filters;

      // `categoryId` (singular-or-array — the app's shape) and `categoryIds`
      // (array-only — shared-web/viewer-extensions' shape) both map to the
      // same wire field. Prefer an explicit `categoryIds` array; otherwise
      // normalize `categoryId`.
      const normalizedCategoryIds = Array.isArray(categoryIds)
        ? categoryIds
        : Array.isArray(categoryId)
          ? categoryId
          : typeof categoryId === "string"
            ? [categoryId]
            : undefined;

      // Union across structures — `includeFilters` is AND across fields but additive within one
      // list, which is exactly the OR-across-structures rule the filter panel needs.
      const classificationNodeIds = Object.values(classifications ?? {}).flat();

      function queryAssets(queryCursorState: CursorState) {
        const requestBody = {
          limit,
          cursorState: queryCursorState,
          query: {
            entityType: "assets_atomic_query_v2",
            includeFilters: {
              ids: Array.isArray(assetIds) ? assetIds : undefined,
              categoryIds: normalizedCategoryIds,
              assignedCategoryIds: Array.isArray(assignedCategoryIds)
                ? assignedCategoryIds
                : undefined,
              statusIds,
              statusLabels,
              locationIds: location ? [location.id] : undefined,
              searchText,
              customAttributes: Object.keys(customFields ?? {}).length
                ? buildCustomFieldFilters(customFields ?? {}, true)
                : undefined,
              classificationNodeIds: classificationNodeIds.length
                ? classificationNodeIds
                : undefined,
              sourceModelLineageUrns,
              recoverable,
              parentSystemIds,
              ancestorSystemIds,
            },
            excludeFilters: selectedAsset.length ? { ids: selectedAsset } : {},
          },
          orderBy,
          options: {
            // These four fields are omitted-when-falsy (not sent as explicit
            // `false`), matching the original app's exact wire behavior —
            // JSON.stringify drops `undefined` but not `false`, so sending
            // `includeDeleted: false` etc. unconditionally would be a real,
            // undocumented behavioral change for any caller that doesn't set
            // them. `includeLastPageLink` is different: the app's own call
            // site always passes an explicit boolean (`isFirstPage`), so it's
            // sent unconditionally by design, not omitted-when-falsy.
            ...(includeDeleted && { includeDeleted: true }),
            ...(location?.includeSubLocations && { includeSubLocations: true }),
            ...(includeCustomFields && { includeCustomAttributes: true }),
            ...(includeTotalResults && { includeTotalResults: true }),
            includeLastPageLink,
            ...(includedStandardFields && {
              includedStandardFields: [...includedStandardFields],
            }),
            ...(includedComputedFields?.length && {
              includedComputedFields,
            }),
            ...(includeStatusCompletionsTracking && {
              includeStatusCompletionsTracking: COMPLETION_TRACKING_INCLUDE,
            }),
            ...(includeClassificationValues && { includeClassificationValues: true }),
          },
        } as BulkAssetQueryRequest;

        return http.post<AssetQueryV2Response>(
          `${http.getAssetsServiceBasePath(projectId, "v2")}/${
            Endpoints.Assets
          }:query`,
          {
            body: JSON.stringify(requestBody),
            signal,
          }
        );
      }
      return fetchAll
        ? (fetchAllPaginatedResults(
            queryAssets
          ) as Promise<AssetQueryV2Response>)
        : queryAssets(cursorState);
    },
    count: async (
      groupBy?: AssetGroupBy,
      cursorState?: CursorState,
      // #2501 — reconciled on `categoryId` (singular-or-array), the app's
      // shape: the wire filter is a single repeatable `filter[categoryId]`
      // query param either way (buildSearchString appends one entry per
      // array element), so `categoryId` is a strict superset of the lib's
      // former array-only `categoryIds` param — no caller loses capability.
      {
        limit,
        categoryId,
        location,
        statusLabels,
        statusIds,
        customFields,
        searchText,
        sourceModelLineageUrns,
        includeDeleted,
        restrictResultGroupsToFilters,
        includeEmptyResultGroups,
      }: Omit<AssetFilters, "includeCustomFields"> & {
        restrictResultGroupsToFilters?: boolean;
        includeEmptyResultGroups?: boolean;
      } = {}
    ): Promise<AssetsCountResponse> => {
      const searchString = buildSearchString({
        groupBy,
        cursorState,
        includeDeleted,
        includeEmptyResultGroups,
        includeSubLocations: location?.includeSubLocations,
        limit,
        restrictResultGroupsToFilters,
        "filter[categoryId]": categoryId,
        "filter[locationId]": location?.id,
        "filter[statusLabel]": statusLabels,
        "filter[statusId]": statusIds,
        "filter[searchText]": searchText,
        "filter[sourceModelLineageUrn]": sourceModelLineageUrns,
        ...(customFields ? buildCustomFieldFilters(customFields) : {}),
      });

      return await http.get<AssetsCountResponse>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${Endpoints.Assets}:count${
          searchString && `?${searchString}`
        }`
      );
    },
    batchGet: async (
      ids: Asset["id"][],
      // #2501 — `includeCustomFields` (default true, matching this method's
      // pre-existing behavior) lets the app's `batchGetById` adapter opt out
      // entirely and reproduce its historical no-query-string request; every
      // other existing caller is unaffected since they never set it.
      options?: { includeDeleted?: boolean; includeCustomFields?: boolean }
    ): Promise<AssetDTO[]> => {
      const searchString = buildSearchString({
        ...(options?.includeCustomFields !== false && {
          includeCustomAttributes: true,
        }),
        ...(options?.includeDeleted && { includeDeleted: true }),
        ...(includeStatusCompletionsTracking && {
          includeStatusCompletionsTracking: COMPLETION_TRACKING_INCLUDE,
        }),
        ...(includeClassificationValues && { includeClassificationValues: true }),
      });

      const generateRequest = (ids: Asset["id"][]) =>
        http.post<{ results: AssetDTO[] }>(
          `${http.getAssetsServiceBasePath(projectId, "v2")}/${
            Endpoints.Assets
          }:batch-get${searchString ? `?${searchString}` : ""}`,
          { body: JSON.stringify({ ids }) }
        );

      if (ids.length > ASSET_MAXIMUM_BATCH_SIZE) {
        const results = [];
        const idChunks = chunk(ids, ASSET_MAXIMUM_BATCH_SIZE);

        for (const chunk of idChunks) {
          results.push(...(await generateRequest(chunk)).results);
        }
        return results;
      }

      return (await generateRequest(ids)).results;
    },

    list: async (
      cursorState?: CursorState,
      filters: AssetFilters = {},
      sort: Sort = { key: "createdAt", order: -1 }
    ): Promise<AssetsPaginatedResponse<AssetDTO[]>> => {
      const {
        limit,
        categoryIds,
        location,
        statusLabels,
        statusIds,
        customFields,
        searchText,
        includeCustomFields,
        includeDeleted,
      } = {
        ...defaultListFilters,
        ...filters,
      };

      const searchString = buildSearchString({
        cursorState,
        includeDeleted,
        includeSubLocations: location?.includeSubLocations,
        limit,
        "filter[categoryId]": categoryIds,
        "filter[locationId]": location?.id,
        "filter[statusLabel]": statusLabels,
        "filter[statusId]": statusIds,
        "filter[searchText]": searchText,
        includeCustomAttributes: includeCustomFields,
        ...(customFields ? buildCustomFieldFilters(customFields) : {}),
        sort: buildSortString(sort),
        ...(includeStatusCompletionsTracking && {
          includeStatusCompletionsTracking: COMPLETION_TRACKING_INCLUDE,
        }),
        ...(includeClassificationValues && { includeClassificationValues: true }),
      });

      return await http.get<AssetsPaginatedResponse<AssetDTO[]>>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${Endpoints.Assets}${
          searchString ? `?${searchString}` : ""
        }`
      );
    },
    create: async (asset: NewAssetDTO): Promise<AssetDTO> =>
      await http.post<AssetDTO>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${Endpoints.Assets}`,
        {
          body: JSON.stringify(asset),
        }
      ),
    batchCreate: async (assets: NewAssetDTO[]): Promise<AssetDTO[]> => {
      const generateRequest = (assets: NewAssetDTO[]) =>
        http.post<AssetsResponse<AssetDTO[]>>(
          `${http.getAssetsServiceBasePath(projectId, "v2")}/${
            Endpoints.Assets
          }:batch-create`,
          {
            body: JSON.stringify(assets),
          }
        );

      if (assets.length > ASSET_MAXIMUM_BATCH_SIZE) {
        const results = [];
        const assetChunks = chunk(assets, ASSET_MAXIMUM_BATCH_SIZE);

        for (const chunk of assetChunks) {
          results.push(...(await generateRequest(chunk)).results);
        }
        return results;
      }

      return (await generateRequest(assets)).results;
    },
    update: async ({
      id,
      ...updates
    }: Pick<Asset, "id"> & AssetUpdate): Promise<AssetDTO> =>
      await http.patch<AssetDTO>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${Endpoints.Assets}/${id}`,
        {
          body: JSON.stringify(updates),
        }
      ),
    delete: async (id: Asset["id"]): Promise<void> =>
      http.del(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${Endpoints.Assets}/${id}`
      ),
    batchDelete: async (assetIds: AssetDTO["id"][]): Promise<void> =>
      await http.post<void>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${
          Endpoints.Assets
        }:batch-delete`,
        {
          body: JSON.stringify({ ids: assetIds }),
        }
      ),
    batchPatch: async (
      assets: Record<UUIDv4, AssetUpdate>
    ): Promise<Record<AssetDTO["id"], AssetDTO>> =>
      await http.patch<Record<AssetDTO["id"], AssetDTO>>(
        `${http.getAssetsServiceBasePath(projectId, "v2")}/${
          Endpoints.Assets
        }:batch-patch`,
        {
          body: JSON.stringify(assets),
        }
      ),
  };
};
